const DAY = 86400000;

const RULES = {
  account: ({ inboxes = [], now = Date.now() }) => {
    const out = [];
    const live = [];
    (inboxes || []).forEach(i => {
      // Disconnected stays per-inbox: each one is separately actionable and a
      // workspace rarely has more than one or two.
      if (i.status === "DISCONNECTED" || i.disconnected) {
        out.push({ tone: "bad", key: "disconnected", label: `${i.email} is disconnected`, text: "Sequences on it are stalled. Reconnect." });
        return;
      }
      live.push(i);
    });

    /*
     * The remaining inbox rules are ROLLED UP. One finding per inbox per rule
     * fanned out until three inboxes with warmup off filled the five-finding
     * cap with the same sentence three times, hiding credits, schedules,
     * unsubscribe and seller-profile findings entirely. Same fix as the
     * low-yield enrichment nudge: name the count, keep one card.
     */
    const rollUp = (matches, key, tone, one, many, text) => {
      if (!matches.length) return;
      out.push({
        tone,
        key,
        label: matches.length === 1 ? one(matches[0]) : many(matches),
        text
      });
    };

    const age = i => (i.connectedTs ? Math.floor((now - i.connectedTs) / DAY) : 999);
    rollUp(
      live.filter(i => age(i) < 14 && i.dailyLimit > 100),
      "new_inbox_high_limit",
      "warn",
      i => `${i.email} sends ${i.dailyLimit}/day on a ${age(i)}-day-old inbox`,
      m => `${m.length} new inboxes send over 100/day`,
      "New inboxes get flagged above ~50/day. Lower it for two weeks."
    );
    rollUp(
      live.filter(i => i.signature !== undefined && !i.signature),
      "no_signature",
      "neutral",
      i => `${i.email} has no signature`,
      m => `${m.length} inboxes have no signature`,
      "Replies drop without a real name and company."
    );
    rollUp(
      live.filter(i => i.warmup === false),
      "warmup_off",
      "warn",
      i => `${i.email} has warmup off`,
      m => `${m.length} inboxes have warmup off`,
      "New inboxes without warmup land in spam. Turn it on for two weeks."
    );
    return out;
  },
  creditUsage: ({ credits = null }) =>
    credits !== null && credits < 50
      ? [{ tone: "warn", key: "low_credits", label: `${credits} credits left`, text: "Enrichment and reveals stop at zero." }]
      : [],
  billing: ({ credits = null }) => RULES.creditUsage({ credits }),
  schedules: ({ schedules }) => {
    const out = Array.isArray(schedules) && !schedules.length
      ? [{ tone: "warn", key: "no_schedule", label: "No sending schedule", text: "Without one, emails go out at 3am in the prospect's timezone." }]
      : [];
    (schedules || [])
      .filter(s => (s.days || []).some(d => /^(sat|sun)/i.test(String(d))))
      .forEach(s => out.push({ tone: "warn", key: "weekend_schedule", label: `${s.name || "A schedule"} sends on weekends`, text: "Weekend cold email gets buried by Monday. Keep it to weekdays." }));
    return out;
  },
  unsubscribe: ({ unsubscribeText }) =>
    unsubscribeText !== undefined && !unsubscribeText
      ? [{ tone: "bad", key: "no_unsubscribe_text", label: "Unsubscribe text not set", text: "Cold email needs it. Set it once here." }]
      : [],
  crm: ({ lastSyncFailed = false }) =>
    lastSyncFailed ? [{ tone: "bad", key: "sync_failed", label: "Last CRM sync failed", text: "Records are drifting. Check the sync log." }] : [],
  "seller-profiles": ({ profiles = [] }) =>
    !(profiles || []).length
      ? [{ tone: "warn", key: "no_profile", label: "No seller profile", text: "Deep research and drafts are generic without one." }]
      : [],
  "domain-health-center": ({ spf = true, dkim = true, dmarc = true }) =>
    [["SPF", spf], ["DKIM", dkim], ["DMARC", dmarc]]
      .filter(([, ok]) => !ok)
      .map(([n]) => ({ tone: "bad", key: `${n.toLowerCase()}_missing`, label: `${n} not set`, text: "Missing records send you to spam. Fix DNS." })),
  doNotContact: ({ domains }) =>
    Array.isArray(domains) && !domains.length
      ? [{ tone: "neutral", key: "no_blocked_domains", label: "No blocked domains", text: "Add competitors and customers so sequences never hit them." }]
      : []
};

// subPage: the segment after /settings/, e.g. "account", "creditUsage".
const settingsFindings = (subPage = "", data = {}) => (RULES[subPage] ? RULES[subPage](data) : []);

export default settingsFindings;
